// Who may read the console. ONE definition, used by the UI gate (src/kit/auth.js, UX only) and by the console-live
// functions (functions/console-live/handler.js, enforcement). The console Firestore rules
// (apps/control-plane/firestore.console.rules, isConsoleReader()) state the SAME rule in the rules language, and
// apps/control-plane/rules-tests/console.rules.test.js runs the same accept/reject matrix against them.
//
// The rule (2026-10-14 handover, Phase 4: "console: open to @syntaxeum.com (domain + hd check)"):
//   a Google identity (firebase.sign_in_provider == "google.com")
//   whose email is VERIFIED (email_verified === true, an absent flag is a refusal)
//   whose email domain is EXACTLY syntaxeum.com (not syntaxeum.com.evil.com, not evilsyntaxeum.com)
//   and whose ID token carries hd == "syntaxeum.com" (the Google Workspace hosted domain)
// OR a verified Google sign-in whose address is on OPERATOR_ALLOWLIST (explicit exceptions to the domain + hd part only;
//    the Google-provider and verified-email requirements apply to everyone).
//
// THE hd CLAIM. Firebase Auth does NOT copy Google's `hd` into the Firebase ID token: the documented token fields are
// email, email_verified, firebase.sign_in_provider, firebase.identities, firebase.tenant and custom claims; nothing else
// from the Google ID token survives the exchange (firebase.sign_in_attributes is SAML/OIDC-provider only). So `hd` here
// is a SESSION CLAIM written by the console-hd blocking function (functions/console-hd/, beforeUserSignedIn), which
// copies the Google profile's `hd` at sign-in. A browser cannot write a custom or session claim; only server code with
// Identity Platform admin can. Until that function is deployed no token carries `hd`, the domain path is closed, and
// only OPERATOR_ALLOWLIST (mark@) gets in: fail closed.
//
// Text handling (CLAUDE.md 5.1): the address and hd must be printable ASCII as given, then are lower-cased. Non-ASCII
// is refused rather than folded, so no Unicode normalisation (NFC, case mapping of e.g. U+212A KELVIN SIGN to "k") can
// turn a lookalike into an accepted address, and the rules (which cannot normalise) agree with this code exactly.

export const OPERATOR_DOMAIN = "syntaxeum.com";

/** Explicit exceptions to the domain rule. mark@ stays so the console keeps working before the hd claim is minted. */
export const OPERATOR_ALLOWLIST = Object.freeze(["mark@syntaxeum.com"]);

/** The architect's keyless service account. Accepted by the console-live functions only (Google OIDC identity token,
 *  audience = the function's URL), read-only. infra/tofu/shared/architect_agent.tf. */
export const ARCHITECT_SERVICE_ACCOUNT = "architect-agent@syntaxeum-control-plane.iam.gserviceaccount.com";

const PRINTABLE_ASCII = /^[\x21-\x7e]{1,254}$/;
const DOMAIN_ADDRESS = /^[a-z0-9._%+-]{1,64}@syntaxeum\.com$/;

/** The lower-cased address, or null if it is not a string of printable ASCII (NFC-stable by construction). */
export function normalizeEmail(email) {
  if (typeof email !== "string" || !PRINTABLE_ASCII.test(email)) return null;
  if (email.normalize("NFC") !== email) return null;
  return email.toLowerCase();
}

/**
 * Decide from DECODED Firebase ID token claims (verifyIdToken's result, or getIdTokenResult().claims in the browser).
 * Only exactly `true` counts as verified.
 */
export function isAllowedOperator(claims) {
  if (!claims || typeof claims !== "object") return false;
  if (claims.email_verified !== true) return false;
  const email = normalizeEmail(claims.email);
  if (!email) return false;
  if (claims.firebase?.sign_in_provider !== "google.com") return false;
  if (OPERATOR_ALLOWLIST.includes(email)) return true;
  if (!DOMAIN_ADDRESS.test(email)) return false;
  const hd = normalizeEmail(claims.hd); // same ASCII-only handling; hd has no "@" so it never matches an address rule
  return hd === OPERATOR_DOMAIN;
}

/**
 * The service-account caller: DECODED claims of a Google-signed OIDC identity token whose signature, issuer, expiry and
 * audience the caller has already checked (google-auth-library). Exactly the architect account, verified.
 */
export function isArchitectServiceAccount(claims) {
  if (!claims || typeof claims !== "object") return false;
  if (claims.email_verified !== true) return false;
  return normalizeEmail(claims.email) === ARCHITECT_SERVICE_ACCOUNT;
}
